// src/pages/Inventory/StockMovements.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/axios";
import {
  FaDownload,
  FaFilePdf,
  FaSyncAlt,
  FaSearch,
  FaMapMarkerAlt,
  FaFilter,
  FaPlus,
  FaHeartbeat,
  FaCheckCircle,
  FaTimesCircle,
  FaLayerGroup,
  FaTrash,
} from "react-icons/fa";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ---------- utils ---------- */
function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
const robustArray = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.data)
    ? data.data
    : [];

const TYPES = [
  { value: "", label: "All types" },
  { value: "receive", label: "Receive" },
  { value: "issue", label: "Issue" },
  { value: "adjust", label: "Adjustment" },
  { value: "transfer", label: "Transfer" },
  { value: "wastage", label: "Wastage" },
];

const typeBadge = (t) => {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border";
  switch ((t || "").toLowerCase()) {
    case "receive":
    case "receipt":
      return base + " bg-emerald-100 text-emerald-700 border-emerald-200";
    case "issue":
      return base + " bg-rose-100 text-rose-700 border-rose-200";
    case "adjust":
      return base + " bg-amber-100 text-amber-700 border-amber-200";
    case "transfer":
      return base + " bg-blue-100 text-blue-700 border-blue-200";
    case "wastage":
      return base + " bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    default:
      return base + " bg-slate-100 text-slate-700 border-slate-200";
  }
};

const dirBadge = (d) => {
  if (!d) return null;
  const cls =
    "ml-2 px-1.5 py-0.5 rounded text-[10px] border align-middle";
  if (d === "in") return <span className={cls + " bg-emerald-100 text-emerald-700 border-emerald-200"}>in</span>;
  if (d === "out") return <span className={cls + " bg-rose-100 text-rose-700 border-rose-200"}>out</span>;
  if (d === "self") return <span className={cls + " bg-slate-100 text-slate-700 border-slate-200"}>self</span>;
  return null;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/* ---------- role helpers (robust) ---------- */
const ALLOWED = new Set(["developer", "admin", "manager", "warehouse"]);

function normalizeToArray(x) {
  if (!x) return [];
  if (Array.isArray(x)) return x;
  if (typeof x === "string") return [x];
  return [];
}

function readRolesFromLocalStorage() {
  try {
    const lsRoles = JSON.parse(localStorage.getItem("roles") || "[]");
    const singleRole = localStorage.getItem("role");
    const extra = JSON.parse(localStorage.getItem("permissions") || "[]");
    const merged = [
      ...normalizeToArray(lsRoles),
      ...normalizeToArray(extra),
      ...(singleRole ? [singleRole] : []),
    ];
    return merged.map((r) => String(r).toLowerCase());
  } catch {
    return [];
  }
}

function readRolesFromJWT() {
  try {
    const tok =
      localStorage.getItem("token") || localStorage.getItem("access_token");
    if (!tok || !tok.includes(".")) return [];
    const payload = JSON.parse(atob(tok.split(".")[1] || ""));
    const candidates = [
      ...normalizeToArray(payload?.roles),
      ...normalizeToArray(payload?.permissions),
      ...normalizeToArray(payload?.scopes),
      ...normalizeToArray(payload?.groups),
      ...(payload?.role ? [payload.role] : []),
      ...normalizeToArray(payload?.user?.roles),
      ...(payload?.user?.role ? [payload.user.role] : []),
      ...normalizeToArray(payload?.user?.permissions),
      ...normalizeToArray(payload?.user?.scopes),
    ];
    return candidates.map((r) => String(r).toLowerCase());
  } catch {
    return [];
  }
}

function computeCanManageLocal() {
  const roles = [...readRolesFromLocalStorage(), ...readRolesFromJWT()];
  return roles.some((r) => ALLOWED.has(r));
}

/* ---------- Create Location Modal (no bins) ---------- */
function CreateLocationModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [active, setActive] = useState(true);
  const [isDefaultGR, setIsDefaultGR] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const canSubmit = String(name).trim().length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const locPayload = {
        name: String(name).trim(),
        code: String(code || "").trim() || undefined,
        active: !!active,
        is_default_goods_received: !!isDefaultGR,
      };
      await api.post("/inventory/locations", locPayload);

      try {
        localStorage.setItem("inventory:pulse", String(Date.now()));
      } catch {}

      onCreated?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create location.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-900">New Location</h2>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
            Close
          </button>
        </div>

        {error && <div className="mb-3 text-sm text-rose-600">{error}</div>}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Name *</label>
            <input
              value={name}
              onChange={(e)=>setName(e.target.value)}
              placeholder="e.g. Kitchen – Hot"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Code (optional)</label>
            <input
              value={code}
              onChange={(e)=>setCode(e.target.value)}
              placeholder="e.g. KTH"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={active} onChange={(e)=>setActive(e.target.checked)} />
              Active
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={isDefaultGR} onChange={(e)=>setIsDefaultGR(e.target.checked)} />
              Default Goods Received (GR)
            </label>
          </div>

          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700">Cancel</button>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm text-white disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Create Location"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- helpers to infer Main & Wastage (fallback) ---------- */
function inferMainName(locations) {
  const lc = (s) => String(s || "").toLowerCase().trim();
  const flagged = locations.find((l) => l.is_default_goods_received && l.name);
  if (flagged) return flagged.name;
  const candidates = ["main store","store","warehouse","central store","goods received"];
  const hit = locations.find((l) => candidates.includes(lc(l.name)));
  return hit?.name || "";
}
function inferWastageName(locations) {
  const lc = (s) => String(s || "").toLowerCase().trim();
  const candidates = ["wastage","waste","damaged","expired"];
  const hit = locations.find((l) => candidates.includes(lc(l.name)));
  return hit?.name || "";
}

/* ---------- Quick Create Movement (single, unchanged) ---------- */
function NewMovementModal({
  open,
  onClose,
  locations,
  onSaved,
  canManageLocations,
  mainNameOverride,
  wastageNameOverride,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [type, setType] = useState("RECEIVE");

  const [itemQuery, setItemQuery] = useState("");
  const dItemQuery = useDebounced(itemQuery, 300);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [pickedItem, setPickedItem] = useState(null);

  const [qty, setQty] = useState("");
  const [fromLoc, setFromLoc] = useState("");
  const [toLoc, setToLoc] = useState("");
  const [reference, setReference] = useState("");

  const [txDate, setTxDate] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  });

  const [onHandByLoc, setOnHandByLoc] = useState({});
  const suggBoxRef = useRef(null);

  const mainName = useMemo(
    () => mainNameOverride || inferMainName(locations),
    [locations, mainNameOverride]
  );
  const wastageName = useMemo(
    () => wastageNameOverride || inferWastageName(locations),
    [locations, wastageNameOverride]
  );

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setError("");
    setType("RECEIVE");
    setItemQuery("");
    setSuggestions([]);
    setShowSuggest(false);
    setPickedItem(null);
    setQty("");
    setFromLoc("");
    setToLoc(mainName || "");
    setReference("");
    const d = new Date();
    d.setSeconds(0, 0);
    setTxDate(
      new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    );
    setOnHandByLoc({});
  }, [open, mainName]);

  useEffect(() => {
    if (!open) return;
    const T = String(type).toUpperCase();
    if (T === "RECEIVE") {
      setFromLoc("");
      setToLoc(mainName || "");
    } else if (T === "ISSUE") {
      setFromLoc(mainName || "");
      setToLoc("");
    } else if (T === "TRANSFER") {
    } else if (T === "WASTAGE") {
      setToLoc(wastageName || "");
      if (fromLoc && fromLoc.toLowerCase() === String(wastageName).toLowerCase()) {
        setFromLoc("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, mainName, wastageName, open]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!open) return;
      const q = String(dItemQuery || "").trim();
      if (!q) {
        if (!cancel) setSuggestions([]);
        return;
      }
      try {
        const { data } = await api.get("/inventory/stock-levels", {
          params: { q, limit: 10, sort: "item_code", dir: "asc" },
        });
        const list = robustArray(data).map((r) => ({
          item_id: r.item_id ?? r.stock_item_id ?? r.id,
          item_code: r.item_code ?? r.code ?? "",
          description: r.description ?? r.stock_name ?? "",
          avg_cost: r.avg_cost ?? r.cost ?? r.unit_price ?? null,
        }));
        if (!cancel) setSuggestions(list);
      } catch {
        if (!cancel) setSuggestions([]);
      }
    })();
    return () => { cancel = true; };
  }, [dItemQuery, open]);

  async function refreshOnHandHints(code) {
    try {
      if (!code) return setOnHandByLoc({});
      const { data } = await api.get("/inventory/stock-levels/breakdown", {
        params: { item_code: code, include_zero: 1 },
      });
      const rows = robustArray(data);
      const map = {};
      rows.forEach((r) => {
        const loc = r.location_name || r.name || "Unspecified";
        const qty = Number(r.on_hand_qty ?? r.qty ?? r.on_hand ?? 0);
        map[loc] = Number.isFinite(qty) ? qty : 0;
      });
      setOnHandByLoc(map);
    } catch {
      setOnHandByLoc({});
    }
  }

  const handlePick = (it) => {
    setPickedItem(it);
    setItemQuery(it.item_code);
    setShowSuggest(false);
    refreshOnHandHints(it.item_code);
  };

  const dCodeGuess = useMemo(() => {
    const guess = (pickedItem?.item_code || dItemQuery || "").trim();
    return /^[\w-]{2,}$/.test(guess) ? guess : "";
  }, [pickedItem, dItemQuery]);
  useEffect(() => {
    if (!open) return;
    if (dCodeGuess) refreshOnHandHints(dCodeGuess);
  }, [open, dCodeGuess]);

  const getAvailAt = (name) => {
    if (!name) return null;
    const n = String(name).trim().toLowerCase();
    for (const k of Object.keys(onHandByLoc)) {
      if (String(k).trim().toLowerCase() === n) {
        const v = Number(onHandByLoc[k]);
        return Number.isFinite(v) ? v : 0;
      }
    }
    return null;
  };

  const softError = useMemo(() => {
    const qn = Number(qty);
    if (!pickedItem?.item_code && !itemQuery.trim()) return "Item code is required";

    if (["RECEIVE","ISSUE"].includes(String(type).toUpperCase()) && !mainName) {
      return "Main Store location is not configured (mark a location 'Default Goods Received' or name it 'Main Store').";
    }
    if (String(type).toUpperCase() === "WASTAGE" && !wastageName) {
      return "Wastage location is not configured (create a location named 'Wastage' / 'Waste').";
    }

    const t = String(type).toUpperCase();
    if (t === "TRANSFER") {
      const fromLC = String(fromLoc || "").trim().toLowerCase();
      const toLC   = String(toLoc   || "").trim().toLowerCase();
      if (fromLC && toLC && fromLC === toLC) return "From and To locations cannot be the same for a transfer.";
      if (!(fromLoc || "").trim()) return "Transfer requires a From location";
      if (!(toLoc   || "").trim()) return "Transfer requires a To location";
      if (!Number.isFinite(qn) || qn <= 0) return "Transfer quantity must be > 0";
      const avail = getAvailAt(fromLoc);
      if (avail != null && qn > avail) return `Not enough stock at "${fromLoc}" (have ${avail}, need ${qn}).`;
    } else if (t === "RECEIVE") {
      if (!Number.isFinite(qn) || qn <= 0) return "Receive quantity must be > 0";
    } else if (t === "ISSUE") {
      if (!Number.isFinite(qn) || qn <= 0) return "Issue quantity must be > 0";
      if (!(toLoc || "").trim()) return "Issue requires a destination";
      if (String(toLoc).toLowerCase() === String(mainName).toLowerCase()) {
        return "Issue destination cannot be Main Store.";
      }
      const avail = getAvailAt(mainName);
      if (avail != null && qn > avail) return `Not enough stock at "${mainName}" (have ${avail}, need ${qn}).`;
    } else if (t === "WASTAGE") {
      if (!Number.isFinite(qn) || qn <= 0) return "Wastage quantity must be > 0";
      if (!(fromLoc || "").trim()) return "Wastage requires a source location";
      const avail = getAvailAt(fromLoc);
      if (avail != null && qn > avail) return `Not enough stock at "${fromLoc}" (have ${avail}, need ${qn}).`;
    } else {
      if (!Number.isFinite(qn)) return "Adjust quantity must be a number (can be negative or positive)";
      if (qn > 0 && !toLoc) return "Positive adjust needs a target location";
      if (qn < 0 && !fromLoc) return "Negative adjust needs a source location";
    }
    return "";
  }, [type, qty, itemQuery, pickedItem, fromLoc, toLoc, onHandByLoc, mainName, wastageName]);

  const hardBlockReason = useMemo(() => {
    const qn = Number(qty);
    if (!Number.isFinite(qn) || qn <= 0) return "";
    const t = String(type).toUpperCase();
    const availIssue = getAvailAt(mainName);
    const availFrom = getAvailAt(fromLoc);
    if (t === "ISSUE" && availIssue != null && qn > availIssue) {
      return `Insufficient stock at "${mainName}" (have ${availIssue}, need ${qn}).`;
    }
    if ((t === "TRANSFER" || t === "WASTAGE") && availFrom != null && qn > availFrom) {
      return `Insufficient stock at "${fromLoc}" (have ${availFrom}, need ${qn}).`;
    }
    if (t === "ADJUST" && qn < 0 && availFrom != null && Math.abs(qn) > availFrom) {
      return `Insufficient stock at "${fromLoc}" for negative adjustment (have ${availFrom}, need ${Math.abs(qn)}).`;
    }
    return "";
  }, [type, qty, fromLoc, onHandByLoc, mainName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (hardBlockReason) return setError(hardBlockReason);
    if (softError) return setError(softError);
    try {
      const T = String(type).toUpperCase();

      const payload = {
        type: T,
        item_code: (pickedItem?.item_code || itemQuery || "").trim(),
        qty: Number(qty),
        reference: reference || undefined,
        tx_date: txDate ? new Date(txDate).toISOString() : undefined,
      };

      if (T === "RECEIVE") {
        payload.to_loc = mainName || undefined;
      } else if (T === "ISSUE") {
        payload.to_loc = toLoc || undefined;
      } else if (T === "TRANSFER") {
        payload.from_loc = fromLoc || undefined;
        payload.to_loc = toLoc || undefined;
      } else if (T === "WASTAGE") {
        payload.from_loc = fromLoc || undefined;
        payload.to_loc = wastageName || undefined;
      } else if (T === "ADJUST") {
        if (Number(qty) > 0) payload.to_loc = toLoc || undefined;
        else payload.from_loc = fromLoc || undefined;
      }

      const resp = await api.post("/inventory/stock-movements", payload);

      try {
        localStorage.setItem("inventory:pulse", String(Date.now()));
      } catch {}

      onSaved?.(resp.data, resp.headers || {});
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save movement.");
    }
  };

  useEffect(() => {
    function onDocClick(e) {
      if (!suggBoxRef.current) return;
      if (!suggBoxRef.current.contains(e.target)) setShowSuggest(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const T = String(type).toUpperCase();
    if (T === "RECEIVE" && !toLoc && mainName) setToLoc(mainName);
    if (T === "WASTAGE" && !toLoc && wastageName) setToLoc(wastageName);
  }, [type, mainName, wastageName, toLoc]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl bg-white border border-slate-200 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">New Stock Movement</h2>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
            Close
          </button>
        </div>

        {(error || hardBlockReason || softError) && (
          <div className="mb-3 text-sm space-y-1">
            {hardBlockReason ? <div className="text-rose-600">{hardBlockReason}</div> :
             error ? <div className="text-rose-600">{error}</div> :
             <div className="text-amber-700">{softError}</div>}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200">
              <option value="RECEIVE">Receive</option>
              <option value="ISSUE">Issue</option>
              <option value="ADJUST">Adjust</option>
              <option value="TRANSFER">Transfer</option>
              <option value="WASTAGE">Wastage</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">Date/Time</label>
            <input type="datetime-local" value={txDate} onChange={(e) => setTxDate(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200" />
          </div>

          <div className="md:col-span-2" ref={suggBoxRef}>
            <label className="block text-xs text-slate-500 mb-1">Item (type code or description)</label>
            <div className="relative">
              <input
                value={itemQuery}
                onChange={(e) => {
                  setItemQuery(e.target.value);
                  setPickedItem(null);
                  setShowSuggest(true);
                }}
                onFocus={() => setShowSuggest(true)}
                placeholder="e.g. MILK-1L"
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200"
              />
              {showSuggest && suggestions.length > 0 && (
                <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                  {suggestions.map((it) => (
                    <button
                      key={it.item_id ?? it.item_code}
                      type="button"
                      onClick={() => handlePick(it)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                    >
                      <div className="font-mono text-slate-900">{it.item_code}</div>
                      <div className="text-xs text-slate-500">
                        {it.description || "—"}
                        {it.avg_cost != null && <span className="ml-2">· est £{Number(it.avg_cost).toFixed(2)}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {pickedItem && <div className="mt-1 text-xs text-slate-500">{pickedItem.description || "—"}</div>}
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Quantity {type === "ADJUST" ? "(+/-)" : ""}
            </label>
            <input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200" />
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">From Location</label>
            <input
              list="locnames"
              value={fromLoc}
              onChange={(e) => setFromLoc(e.target.value)}
              placeholder={String(type).toUpperCase() === "ISSUE" ? (mainName ? `Main: ${mainName}` : "Main Store") : "Start typing or pick…"}
              className={`w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 ${String(type).toUpperCase() === "ISSUE" ? "opacity-70 cursor-not-allowed" : ""}`}
              disabled={String(type).toUpperCase() === "ISSUE"}
            />
            {String(type).toUpperCase() === "ISSUE" && (
              <div className="mt-1 text-xs text-slate-500">From is enforced to Main Store.</div>
            )}
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">To Location</label>
            <input
              list="locnames"
              value={toLoc}
              onChange={(e) => setToLoc(e.target.value)}
              placeholder={
                String(type).toUpperCase() === "RECEIVE" && mainName
                  ? `Default: ${mainName}`
                  : String(type).toUpperCase() === "WASTAGE" && wastageName
                  ? `Default: ${wastageName}`
                  : "Start typing or pick…"
              }
              className={`w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200 ${["RECEIVE","WASTAGE"].includes(String(type).toUpperCase()) ? "opacity-70 cursor-not-allowed" : ""}`}
              disabled={["RECEIVE","WASTAGE"].includes(String(type).toUpperCase())}
            />
          </div>

          <datalist id="locnames">
            {locations.map((l) =>
              (l.name ? [l.name] : [])
                .filter(Boolean)
                .map((n) => <option key={`${l.location_id}-${n}`} value={n} />)
            )}
          </datalist>

          <div className="md:col-span-2">
            <label className="block text-xs text-slate-500 mb-1">Reference</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="PO#, reason, etc." className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200" />
          </div>

          {pickedItem && Object.keys(onHandByLoc).length > 0 && (
            <div className="md:col-span-2 text-xs text-slate-600">
              <div className="mb-1">On-hand by location (hint):</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(onHandByLoc).map(([name, val]) => (
                  <span key={name} className="px-2 py-1 rounded border border-slate-200 bg-slate-50" title="Backend enforces the true balance.">
                    {name}: <span className="font-mono text-slate-900">{Number(val)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700">
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm inline-flex items-center gap-2 text-white"
              title="Save movement"
            >
              Save Movement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- NEW: Batch Movements Modal ---------- */
function BatchMovementModal({
  open,
  onClose,
  locations,
  mainNameOverride,
  wastageNameOverride,
  onSaved,
}) {
  const [type, setType] = useState("ISSUE");
  const [fromLoc, setFromLoc] = useState("");
  const [toLoc, setToLoc] = useState("");
  const [reference, setReference] = useState("");
  const [txDate, setTxDate] = useState(() => {
    const d = new Date();
    d.setSeconds(0, 0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  });
  const [partialMode, setPartialMode] = useState(false);

  const [lines, setLines] = useState([{ item_code: "", qty: "" }]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mainName = useMemo(
    () => mainNameOverride || inferMainName(locations),
    [locations, mainNameOverride]
  );
  const wastageName = useMemo(
    () => wastageNameOverride || inferWastageName(locations),
    [locations, wastageNameOverride]
  );

  useEffect(() => {
    if (!open) return;
    setType("ISSUE");
    setFromLoc(mainName || "");
    setToLoc("");
    setReference("");
    setPartialMode(false);
    setLines([{ item_code: "", qty: "" }]);
    setError("");
    const d = new Date();
    d.setSeconds(0, 0);
    setTxDate(
      new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    );
  }, [open, mainName]);

  useEffect(() => {
    const T = String(type).toUpperCase();
    if (T === "RECEIVE") {
      setFromLoc("");
      setToLoc(mainName || "");
    } else if (T === "ISSUE") {
      setFromLoc(mainName || "");
      setToLoc("");
    } else if (T === "TRANSFER") {
    } else if (T === "WASTAGE") {
      setFromLoc("");
      setToLoc(wastageName || "");
    }
  }, [type, mainName, wastageName]);

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, { item_code: "", qty: "" }]);
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const debouncers = useRef({});
  const fetchSuggestions = (idx, q) => {
    if (!q) return updateLine(idx, { _suggestions: [], _suggestOpen: false });
    clearTimeout(debouncers.current[idx]);
    debouncers.current[idx] = setTimeout(async () => {
      try {
        const { data } = await api.get("/inventory/stock-levels", {
          params: { q, limit: 8, sort: "item_code", dir: "asc" },
        });
        const list = robustArray(data).map((r) => ({
          item_code: r.item_code ?? r.code ?? "",
          description: r.description ?? r.stock_name ?? "",
        }));
        updateLine(idx, { _suggestions: list, _suggestOpen: true });
      } catch {
        updateLine(idx, { _suggestions: [], _suggestOpen: false });
      }
    }, 250);
  };

  const canSubmit = useMemo(() => {
    if (!lines.length) return false;
    for (const l of lines) {
      const code = String(l.item_code || "").trim();
      const qn = Number(l.qty);
      if (!code) return false;
      if (!Number.isFinite(qn) || qn <= 0) return false;
    }
    const T = String(type).toUpperCase();
    if (T === "ISSUE") return !!(toLoc || "").trim();
    if (T === "TRANSFER") return !!(fromLoc || "").trim() && !!(toLoc || "").trim();
    if (T === "RECEIVE") return true;
    if (T === "WASTAGE") return !!(fromLoc || "").trim();
    return true;
  }, [lines, type, fromLoc, toLoc]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) {
      return setError("Please complete all required fields and line items.");
    }
    setSubmitting(true);
    try {
      const T = String(type).toUpperCase();

      const defaults = {
        reference: reference || undefined,
        tx_date: txDate ? new Date(txDate).toISOString() : undefined,
      };
      if (T === "ISSUE") {
        defaults.to_loc = toLoc || undefined;
      } else if (T === "TRANSFER") {
        defaults.from_loc = fromLoc || undefined;
        defaults.to_loc = toLoc || undefined;
      } else if (T === "RECEIVE") {
        defaults.to_loc = mainName || undefined;
      } else if (T === "WASTAGE") {
        defaults.from_loc = fromLoc || undefined;
        defaults.to_loc = wastageName || undefined;
      }

      const cleanLines = lines
        .map((l) => ({
          item_code: String(l.item_code || "").trim(),
          qty: Number(l.qty),
          idempotency_key: undefined,
        }))
        .filter((l) => l.item_code && Number.isFinite(l.qty) && l.qty > 0);

      const payload = {
        type: T,
        defaults,
        lines: cleanLines,
        mode: partialMode ? "partial" : "all-or-nothing",
      };

      const resp = await api.post("/inventory/stock-movements/batch", payload);

      onSaved?.(resp.data, resp.headers || {});
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save batch movements.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm">
      <div className="w-full max-w-4xl rounded-2xl bg-white border border-slate-200 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-slate-900">
            <FaLayerGroup /> Batch Stock Movements
          </h2>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
            Close
          </button>
        </div>

        {error && <div className="mb-3 text-sm text-rose-600">{error}</div>}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-1">
              <label className="block text-xs text-slate-500 mb-1">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900"
              >
                <option value="RECEIVE">Receive</option>
                <option value="ISSUE">Issue</option>
                <option value="TRANSFER">Transfer</option>
                <option value="WASTAGE">Wastage</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Date/Time</label>
              <input
                type="datetime-local"
                value={txDate}
                onChange={(e) => setTxDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Reference (applies to all lines)</label>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="PO#/reason/etc."
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">From Location</label>
              <input
                list="locnames"
                value={fromLoc}
                onChange={(e) => setFromLoc(e.target.value)}
                placeholder={
                  String(type).toUpperCase() === "ISSUE"
                    ? (mainName ? `Main: ${mainName}` : "Main Store")
                    : String(type).toUpperCase() === "WASTAGE"
                    ? "Required for Wastage"
                    : "Optional / as needed"
                }
                className={`w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 ${String(type).toUpperCase() === "ISSUE" ? "opacity-70 cursor-not-allowed" : ""}`}
                disabled={String(type).toUpperCase() === "ISSUE"}
              />
              {String(type).toUpperCase() === "ISSUE" && (
                <div className="mt-1 text-xs text-slate-500">From is enforced to Main Store.</div>
              )}
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">To Location</label>
              <input
                list="locnames"
                value={toLoc}
                onChange={(e) => setToLoc(e.target.value)}
                placeholder={
                  String(type).toUpperCase() === "RECEIVE" && mainName
                    ? `Default: ${mainName}`
                    : String(type).toUpperCase() === "WASTAGE" && wastageName
                    ? `Default: ${wastageName}`
                    : "Required for Issue/Transfer"
                }
                className={`w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 ${["RECEIVE","WASTAGE"].includes(String(type).toUpperCase()) ? "opacity-70 cursor-not-allowed" : ""}`}
                disabled={["RECEIVE","WASTAGE"].includes(String(type).toUpperCase())}
              />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={partialMode} onChange={(e)=>setPartialMode(e.target.checked)} />
                Allow partial (skip invalid lines)
              </label>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full table-auto text-sm">
              <thead>
                <tr className="bg-slate-100 text-left text-slate-700">
                  <th className="p-3 border-b border-slate-200">#</th>
                  <th className="p-3 border-b border-slate-200">Item Code (or search)</th>
                  <th className="p-3 border-b border-slate-200 text-right">Qty</th>
                  <th className="p-3 border-b border-slate-200 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 align-top">
                    <td className="p-3 border-b border-slate-200 text-slate-700">{idx + 1}</td>
                    <td className="p-3 border-b border-slate-200">
                      <div className="relative">
                        <input
                          value={l.item_code}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateLine(idx, { item_code: v });
                            fetchSuggestions(idx, v);
                          }}
                          onFocus={() => updateLine(idx, { _suggestOpen: !!(l._suggestions?.length) })}
                          placeholder="e.g. FRU-001"
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900"
                        />
                        {l._suggestOpen && (l._suggestions?.length || 0) > 0 && (
                          <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white shadow-xl">
                            {l._suggestions.map((s) => (
                              <button
                                key={`${s.item_code}-${idx}`}
                                type="button"
                                onClick={() => updateLine(idx, { item_code: s.item_code, _suggestOpen: false })}
                                className="w-full text-left px-3 py-2 hover:bg-slate-50"
                              >
                                <div className="font-mono text-slate-900">{s.item_code}</div>
                                <div className="text-xs text-slate-500">{s.description || "—"}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3 border-b border-slate-200 text-right">
                      <input
                        type="number"
                        step="any"
                        value={l.qty}
                        onChange={(e) => updateLine(idx, { qty: e.target.value })}
                        className="w-32 text-right bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900"
                        placeholder="0"
                      />
                    </td>
                    <td className="p-3 border-b border-slate-200">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-600"
                        title="Remove line"
                        disabled={lines.length === 1}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addLine}
              className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
            >
              + Add Line
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm disabled:opacity-60 inline-flex items-center gap-2 text-white"
              >
                {submitting && <FaSyncAlt className="animate-spin" />}
                Save Batch
              </button>
            </div>
          </div>
        </form>

        <datalist id="locnames">
          {locations.map((l) =>
            (l.name ? [l.name] : [])
              .filter(Boolean)
              .map((n) => <option key={`${l.location_id}-${n}`} value={n} />)
          )}
        </datalist>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function StockMovementsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [serverTotals, setServerTotals] = useState({
    qty_in: 0,
    qty_out: 0,
    qty_transfer: 0,
  });
  const [totalCount, setTotalCount] = useState(0);

  const [q, setQ] = useState("");
  const dq = useDebounced(q, 350);
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());
  const [locationId, setLocationId] = useState("");
  const [type, setType] = useState("");

  const [locations, setLocations] = useState([]);

  const [specialMain, setSpecialMain] = useState("");
  const [specialWastage, setSpecialWastage] = useState("");

  const [health, setHealth] = useState({ status: "idle", db: "", host: "", user: "", err: "" });
  const [lastWriteMeta, setLastWriteMeta] = useState(null);

  const [sort, setSort] = useState("tx_date");
  const [dir, setDir] = useState("desc");

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  const [showModal, setShowModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showCreateLoc, setShowCreateLoc] = useState(false);

  const [canManageLocations, setCanManageLocations] = useState(() => computeCanManageLocal());

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => {
        const candidates = [
          ...normalizeToArray(data?.roles),
          ...normalizeToArray(data?.permissions),
          ...normalizeToArray(data?.scopes),
          ...normalizeToArray(data?.groups),
          ...(data?.role ? [data.role] : []),
          ...normalizeToArray(data?.user?.roles),
          ...(data?.user?.role ? [data.user.role] : []),
          ...normalizeToArray(data?.user?.permissions),
          ...normalizeToArray(data?.user?.scopes),
        ].map((r) => String(r).toLowerCase());
        setCanManageLocations((prev) => prev || candidates.some((r) => ALLOWED.has(r)));
      })
      .catch(() => {});
  }, []);

  const loadLocations = async () => {
    try {
      const { data } = await api.get("/inventory/locations", {
        params: { active_only: 1 },
      });
      setLocations(Array.isArray(data) ? data : []);
    } catch {
      setLocations([]);
    }
  };
  useEffect(() => { loadLocations(); }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/inventory/special-locations");
        if (cancel) return;
        setSpecialMain(String(data?.main_store?.name || ""));
        setSpecialWastage(String(data?.wastage?.name || ""));
      } catch {
      }
    })();
    return () => { cancel = true; };
  }, []);

  const fetchMovements = async ({ reset = false } = {}) => {
    if (reset) {
      setLoading(true);
      setPage(1);
    } else {
      setRefreshing(true);
    }
    setError("");

    try {
      const params = {
        q: dq.trim() || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        location_id: locationId ? Number(locationId) : undefined,
        per_location: locationId ? 1 : undefined,
        type: type === "wastage" ? "transfer" : (type || undefined),
        sort: sort || undefined,
        dir: dir || undefined,
        page: reset ? 1 : page,
        page_size: PAGE_SIZE,
      };

      const { data } = await api.get("/inventory/stock-movements", { params });

      const items = Array.isArray(data?.items) ? data.items : robustArray(data);

      const normalized = items.map((r) => {
        const baseType = (r.type || r.movement_type || "").toLowerCase();
        const displayType = String(r.display_type || baseType).toLowerCase();
        return {
          movement_id: r.movement_id ?? r.id,
          tx_date: r.tx_date ?? r.created_at ?? r.date ?? null,
          type: displayType,
          item_id: r.item_id ?? r.stock_item_id ?? r.id,
          item_code: r.item_code ?? r.code ?? "",
          description: r.description ?? r.item_description ?? "",
          qty_in: Number(r.qty_in ?? 0),
          qty_out: Number(r.qty_out ?? 0),
          from_loc: r.from_loc ?? r.source_location ?? null,
          to_loc: r.to_loc ?? r.location_name ?? r.dest_location ?? null,
          reference: r.reference ?? null,
          user_name: r.user_name ?? null,
          direction: r.direction || null,
        };
      });

      const filtered = type === "wastage"
        ? normalized.filter((r) => r.type === "wastage")
        : normalized;

      if (reset) setRows(filtered);
      else setRows((prev) => [...prev, ...filtered]);

      setServerTotals({
        qty_in: Number(data?.totals?.qty_in ?? 0),
        qty_out: Number(data?.totals?.qty_out ?? 0),
        qty_transfer: Number(data?.totals?.qty_transfer ?? 0),
      });

      setTotalCount(Number(data?.total ?? (reset ? filtered.length : rows.length + filtered.length)));

      const nextPage = reset ? 2 : page + 1;
      setPage(nextPage);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load stock movements.");
      if (reset) {
        setRows([]);
        setServerTotals({ qty_in: 0, qty_out: 0, qty_transfer: 0 });
        setTotalCount(0);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMovements({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq, dateFrom, dateTo, locationId, type, sort, dir]);

  const handleRefresh = () => fetchMovements({ reset: true });
  const loadMore = () => fetchMovements({ reset: false });

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(key);
      setDir("asc");
    }
  };
  const sortIndicator = (key) =>
    sort === key ? (dir === "asc" ? " ▲" : " ▼") : "";

  const clientTotals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.in += Number(r.qty_in) || 0;
        acc.out += Number(r.qty_out) || 0;
        return acc;
      },
      { in: 0, out: 0 }
    );
  }, [rows]);

  const hasMore = rows.length < totalCount;

  const exportCSV = () => {
    const header = [
      "Date",
      "Type",
      "Item Code",
      "Description",
      "From",
      "To / Location",
      "Qty In",
      "Qty Out",
      "Reference",
      "User",
    ];
    const body = rows.map((r) => [
      r.tx_date ? new Date(r.tx_date).toLocaleString() : "",
      r.type,
      r.item_code,
      r.description,
      r.from_loc ?? "",
      r.to_loc ?? "",
      r.qty_in ?? 0,
      r.qty_out ?? 0,
      r.reference ?? "",
      r.user_name ?? "",
    ]);
    const csv = [header, ...body]
      .map((row) =>
        row.map((f) => `"${String(f ?? "").replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    saveAs(new Blob([csv], { type: "text/csv; charset=utf-8" }), "stock_movements.csv");
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text("Stock Movements", 14, 14);
    autoTable(doc, {
      startY: 18,
      head: [[
        "Date",
        "Type",
        "Item Code",
        "Description",
        "From",
        "To / Location",
        "Qty In",
        "Qty Out",
        "Reference",
        "User",
      ]],
      body: rows.map((r) => [
        r.tx_date ? new Date(r.tx_date).toLocaleString() : "",
        r.type,
        r.item_code,
        r.description,
        r.from_loc ?? "",
        r.to_loc ?? "",
        r.qty_in ?? 0,
        r.qty_out ?? 0,
        r.reference ?? "",
        r.user_name ?? "",
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 34, 34] },
    });
    doc.save("stock_movements.pdf");
  };

  const checkWiring = async () => {
    setHealth({ status: "loading", db: "", host: "", user: "", err: "" });
    try {
      const { data, headers } = await api.get("/inventory/stock-movements/health");
      setHealth({
        status: "ok",
        db: data?.db || "",
        host: data?.host || "",
        user: data?.user || "",
        err: "",
        hdr: headers?.["x-db"] || "",
      });
    } catch (e) {
      setHealth({
        status: "error",
        db: "",
        host: "",
        user: "",
        err: e?.response?.data?.error || e?.message || "Health check failed",
      });
    }
  };

  const showCount = type === "wastage"
    ? `${rows.length.toLocaleString("en-GB")} wastage movement(s)`
    : `${rows.length.toLocaleString("en-GB")} of ${totalCount.toLocaleString("en-GB")} movement(s)`;

  const perspectiveHint = locationId
    ? ` · Perspective @ ${locations.find(l => Number(l.location_id) === Number(locationId))?.name || "location"}`
    : "";

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 text-slate-900">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold text-slate-900">📦 Stock Movements</h1>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={checkWiring}
            className="px-3 py-2 rounded-lg text-sm flex items-center gap-2 border border-slate-200 bg-white text-slate-700 shadow-sm"
            title="Check API wiring and DB target"
          >
            <FaHeartbeat />
            Check Wiring
          </button>
          {health.status === "ok" && (
            <span className="text-xs inline-flex items-center gap-1 text-emerald-700">
              <FaCheckCircle />
              {health.db}@{health.host}
            </span>
          )}
          {health.status === "error" && (
            <span className="text-xs inline-flex items-center gap-1 text-rose-600" title={health.err}>
              <FaTimesCircle />
              wiring error
            </span>
          )}

          {lastWriteMeta?.updated === "1" && (
            <span className="text-xs inline-flex items-center gap-1 text-emerald-700">
              <FaCheckCircle />
              Saved ✓ {lastWriteMeta.db ? `(${lastWriteMeta.db})` : ""}
            </span>
          )}

          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search code/description/reference…"
              className="pl-9 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm"
              title="From"
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm"
              title="To"
            />
          </div>

          <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm"
              title="Filter by location"
            >
              <option value="">All locations</option>
              {locations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="pl-9 pr-8 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm"
              title="Filter by movement type"
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <button onClick={exportCSV} className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm">
            <FaDownload /> CSV
          </button>
          <button onClick={exportPDF} className="bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm">
            <FaFilePdf /> PDF
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-sky-600 hover:bg-sky-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 text-white shadow-sm"
          >
            <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="bg-violet-600 hover:bg-violet-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm"
            title="Create a new stock movement"
          >
            <FaPlus /> New Movement
          </button>

          <button
            onClick={() => setShowBatchModal(true)}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm"
            title="Create multiple movements at once"
          >
            <FaLayerGroup /> Batch
          </button>

          {canManageLocations && (
            <button
              onClick={() => setShowCreateLoc(true)}
              className="bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg text-sm text-white shadow-sm"
              title="Create a new inventory location (admins/managers only)"
            >
              + New Location
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 text-sm text-slate-600">
        Showing {showCount}{perspectiveHint} ·{" "}
        <span className="text-emerald-700 font-medium">
          Qty In: {(serverTotals.qty_in ?? clientTotals.in).toLocaleString("en-GB")}
        </span>{" "}
        ·{" "}
        <span className="text-rose-700 font-medium">
          Qty Out: {(serverTotals.qty_out ?? clientTotals.out).toLocaleString("en-GB")}
        </span>{" "}
        ·{" "}
        <span className="text-blue-700 font-medium">
          Transfers: {(serverTotals.qty_transfer ?? 0).toLocaleString("en-GB")}
        </span>
      </div>

      {error && <div className="mb-4 text-sm text-rose-600">{error}</div>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="bg-slate-100 text-left text-slate-700">
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort("tx_date")} title="Sort by Date">
                Date{sortIndicator("tx_date")}
              </th>
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort("type")} title="Sort by Type">
                Type{sortIndicator("type")}
              </th>
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort("item_code")} title="Sort by Item Code">
                Item Code{sortIndicator("item_code")}
              </th>
              <th className="p-3 border-b border-slate-200">Description</th>
              <th className="p-3 border-b border-slate-200">Location</th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort("qty_in")} title="Sort by Qty In">
                Qty In{sortIndicator("qty_in")}
              </th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort("qty_out")} title="Sort by Qty Out">
                Qty Out{sortIndicator("qty_out")}
              </th>
              <th className="p-3 border-b border-slate-200">Reference</th>
              <th className="p-3 border-b border-slate-200">User</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="p-6 text-center text-slate-500" colSpan={9}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="p-6 text-center text-slate-500" colSpan={9}>
                  No movements found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={`${r.movement_id}-${r.item_id}-${r.tx_date}`} className="hover:bg-slate-50">
                  <td className="p-3 border-b border-slate-200 text-slate-700">
                    {r.tx_date ? new Date(r.tx_date).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 border-b border-slate-200">
                    <span className={typeBadge(r.type)}>{r.type}</span>
                  </td>
                  <td className="p-3 border-b border-slate-200 text-slate-800">{r.item_code || "—"}</td>
                  <td className="p-3 border-b border-slate-200 text-slate-700">{r.description || "—"}</td>
                  <td className="p-3 border-b border-slate-200 text-slate-700">
                    {(r.type === "transfer" || r.type === "wastage")
                      ? `${r.from_loc ?? "—"} → ${r.to_loc ?? "—"}`
                      : r.to_loc ?? r.from_loc ?? "—"}
                    {locationId ? dirBadge(r.direction) : null}
                  </td>
                  <td className="p-3 border-b border-slate-200 text-right text-slate-700">
                    {r.qty_in ? Number(r.qty_in).toLocaleString("en-GB") : "—"}
                  </td>
                  <td className="p-3 border-b border-slate-200 text-right text-slate-700">
                    {r.qty_out ? Number(r.qty_out).toLocaleString("en-GB") : "—"}
                  </td>
                  <td className="p-3 border-b border-slate-200 text-slate-700">{r.reference || "—"}</td>
                  <td className="p-3 border-b border-slate-200 text-slate-700">{r.user_name || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && rows.length > 0 && (
        <div className="flex items-center justify-center mt-4">
          <button
            onClick={loadMore}
            disabled={!hasMore || refreshing}
            className="bg-white hover:bg-slate-50 border border-slate-200 px-4 py-2 rounded-lg text-sm disabled:opacity-60 text-slate-700 shadow-sm"
            title={hasMore ? "Load more" : "No more results"}
          >
            {refreshing ? "Loading…" : hasMore ? "Load more" : "No more results"}
          </button>
        </div>
      )}

      <NewMovementModal
        open={showModal}
        onClose={() => setShowModal(false)}
        locations={locations}
        canManageLocations={computeCanManageLocal()}
        onSaved={(_, headers) => {
          setLastWriteMeta({
            db: headers?.["x-db"] || "",
            updated: headers?.["x-inventory-updated"] || "",
          });
          fetchMovements({ reset: true });
        }}
        mainNameOverride={specialMain}
        wastageNameOverride={specialWastage}
      />

      <BatchMovementModal
        open={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        locations={locations}
        mainNameOverride={specialMain}
        wastageNameOverride={specialWastage}
        onSaved={(_, headers) => {
          setLastWriteMeta({
            db: headers?.["x-db"] || "",
            updated: headers?.["x-inventory-updated"] || "",
          });
          fetchMovements({ reset: true });
        }}
      />

      <CreateLocationModal
        open={showCreateLoc}
        onClose={() => setShowCreateLoc(false)}
        onCreated={() => {
          loadLocations();
          fetchMovements({ reset: true });
        }}
      />
    </div>
  );
}